import type { SettingsVisibility } from "@redotech/redo-merchant-app-common/settings/use-settings-visibility";
import type {
  SettingsPageId,
  SettingsSectionId,
} from "@redotech/redo-settings-common/settings-visibility/settings-pages";
import {
  SettingItem,
  SettingsSection,
  SettingsSectionEnum,
} from "@redotech/redo-web/navigation/redo-settings-nav";

/** A nav item tied to its registry page; the only way an item enters the settings nav. */
export interface SettingsNavPage<S extends SettingsSectionId> {
  page: Extract<SettingsPageId, `${S}/${string}`>;
  item: SettingItem;
}

/** Keyed by id and link because the nav copies items when it adds search terms. */
const PAGE_OF_ITEM = new Map<string, SettingsPageId>();

function itemKey(item: SettingItem): string {
  return `${item.id} ${item.href ?? ""}`;
}

export function navPage<S extends SettingsSectionId>(
  page: Extract<SettingsPageId, `${S}/${string}`>,
  item: SettingItem,
): SettingsNavPage<S> {
  PAGE_OF_ITEM.set(itemKey(item), page);
  return { page, item };
}

/** The registry page a rendered nav item came from, for specs. */
export function registryPageOfNavItem(
  item: SettingItem,
): SettingsPageId | undefined {
  return PAGE_OF_ITEM.get(itemKey(item));
}

/** The items whose registry pages are visible, in order. */
export function visibleNavPages<S extends SettingsSectionId>(
  visibility: SettingsVisibility,
  pages: readonly SettingsNavPage<S>[],
): SettingItem[] {
  return pages
    .filter((entry) => visibility.page(entry.page))
    .map((entry) => entry.item);
}

/** A nav section with only the pages the registry shows for this merchant. */
export function settingsSection<S extends SettingsSectionId>(
  visibility: SettingsVisibility,
  section: S,
  name: SettingsSectionEnum,
  pages: readonly SettingsNavPage<S>[],
): SettingsSection | undefined {
  if (!visibility.section(section)) {
    return undefined;
  }
  const visible = visibleNavPages(visibility, pages);
  if (visible.length === 0) {
    return undefined;
  }
  return { name, items: visible };
}
